<script lang="ts">
	import { onDestroy, onMount, tick } from 'svelte';
	import { Deferred } from 'overline';
	import range from 'lodash/range.js';
	import debounce from 'lodash/debounce.js';
	import spinner from '@local/assets/icons/spinner-lg.svg?url';
	import fitColumns from './fit-columns.js';
	import rotatePoint from './rotate-point.js';
	import { getLogger } from '@diagraphics/logging';

	import { version } from 'openseadragon/package.json';

	import type { Viewer, TileSource } from 'openseadragon';
	import type { ControlBus, OSDController } from './OSDController.js';

	const log = getLogger('OpenSeadragon', 'DEBUG');

	log.info(`Using OpenSeadragon v${version}`);

	interface Page {
		label?: string;
		rotation?: 0 | 90 | 180 | 270;
		tileSource: TileSource | Promise<TileSource>;
	}

	type Pages = Array<Page>;

	export let pages: Pages;

	/**
	 * @type {Options & {pannable?: boolean, reorderable?: boolean}}
	 */
	export let options = {};
	export let debug = false;
	export let pannable = true;
	export let controlBus: ControlBus = null;
	export let tileOverlay = null;
	export let overlay = null;
	export let initialPage = null;
	export let initialBounds = null;
	export let layout = 'grid';
	/**
	 * @type Array<{ x: number, y: number, index: number }
	 */
	export let spinners = [];

	export const tile = (index) => {
		if (!tileFunc) return;
		return tileFunc(index);
	};
	export const tileExtents = (index) => {
		if (!tileExtentsFunc) return;
		return tileExtentsFunc(index);
	};
	export const rotatedCoords = (tileIndex, tileCoords) => {
		if (!orientToTileFunc) return;
		return orientToTileFunc(tileIndex, tileCoords);
	};
	export const unrotatedCoords = (tileIndex, rotatedCoords) => {
		if (!orientFromTileFunc) return;
		return orientFromTileFunc(tileIndex, rotatedCoords);
	};
	export const nearestTile = (pixelCoords) => {
		if (!nearestTileFunc) return;
		return nearestTileFunc(pixelCoords);
	};
	export const isPointVisible = (point) => {
		if (!isPointVisibleFunc) return;
		return isPointVisibleFunc(point);
	};
	export const pixelCoords = (tileCoords) => {
		if (!tileCoordsConvFunc) return;
		return tileCoordsConvFunc(tileCoords);
	};
	export const tileCoords = (pixelCoords, tileIndex) => {
		if (!pixelCoordsConvFunc) return;
		return pixelCoordsConvFunc(pixelCoords, tileIndex);
	};
	export const getController = () => {
		if (!controller) return;
		return controller;
	};

	const ready = new Deferred();
	const fullyLoaded = new Deferred();

	let width: number;
	let height: number;
	let prevTileSources;
	let element: HTMLDivElement;
	let viewer: Viewer;
	let hitCanvases: Array<HTMLCanvasElement> = [];
	let hitContexts: Array<CanvasRenderingContext2D> = [];
	let overallHitCanvas: HTMLCanvasElement | null = null;
	let overallHitContext: CanvasRenderingContext2D | null = null;
	let controller: OSDController;
	let Point;
	let Placement;
	let tileFunc = null;
	let tileExtentsFunc = null;
	let orientToTileFunc = null;
	let orientFromTileFunc = null;
	let nearestTileFunc = null;
	let isPointVisibleFunc = null;
	let tileCoordsConvFunc = null;
	let pixelCoordsConvFunc = null;
	let isFullyLoaded = false;

	function updatePannable(pannable) {
		if (controller) {
			controller.viewer.panHorizontal = pannable;
			controller.viewer.panVertical = pannable;
		}
	}

	async function resolveTileSources(pages) {
		return Promise.all(pages.map((page) => page.tileSource));
	}

	function sourceKey(tileSource) {
		// TODO: is there a way to make this more robust?
		if (tileSource?.['@context'] === 'http://iiif.io/api/image/2/context.json') {
			const url = new URL(tileSource['@id']);
			const components = url.pathname.split('/');
			const path = components.slice(-2).join('%2F');
			return url.origin + '/iiif/2/drawings/' + path + '/info.json';
		}

		return tileSource;
	}

	async function updateTileSources(pages) {
		if (viewer) {
			const totalItems = viewer.world.getItemCount();
			const resolvedSources = await resolveTileSources(pages);

			const resetView = totalItems !== resolvedSources?.length;
			let reArrange = false;
			if (resolvedSources?.length) {
				// Get indices of existing tile sources
				const tileSourceMap = new Map();
				resolvedSources.forEach((source, i) => {
					tileSourceMap.set(source, null);
				});

				const existingItems = [];
				for (let i = 0; i < totalItems; i += 1) {
					const existing = viewer.world.getItemAt(i);
					existingItems.push(existing);

					const key = sourceKey(existing.source);
					if (tileSourceMap.has(key)) {
						tileSourceMap.set(key, i);
					}
				}

				resolvedSources.forEach((tileSource, index) => {
					// Update hitCanvases
					if (!hitCanvases[index]) {
						hitCanvases[index] = document.createElement('canvas');
						const context = hitCanvases[index].getContext('2d');
						context.imageSmoothingEnabled = false;
						hitContexts[index] = context;
					}

					const existingIndex = tileSourceMap.get(tileSource);
					const existing = viewer.world.getItemAt(index);
					if (existingIndex !== null && existing) {
						if (existingIndex !== index) {
							reArrange = true;
							const item = existingItems[existingIndex];
							viewer.world.setItemIndex(item, index);
						}
					} else {
						viewer.addTiledImage({
							tileSource,
							index,
							replace: existing ? true : false
						});
					}
				});

				// Remove any extra hitCanvases if necessary
				if (hitCanvases.length > resolvedSources.length) {
					hitCanvases.splice(resolvedSources.length);
				}

				if (totalItems > resolvedSources.length) {
					for (let i = totalItems - 1; i > resolvedSources.length - 1; i -= 1) {
						const item = viewer.world.getItemAt(i);
						if (item) {
							viewer.world.removeItem(item);
						}
					}
				}

				prevTileSources = resolvedSources;

				if (resetView) {
					const ar = width / height;
					const cols = layout === 'grid' ? fitColumns(ar, resolvedSources.length) : 1;
					controller.updateCollectionLayout({ columns: cols });
					viewer.viewport.goHome();
					viewer.forceRedraw();
				} else if (totalItems !== resolvedSources.length || reArrange) {
					controller.arrange({ immediately: true, columns: viewer.collectionColumns });
				}

				const labels = pages.map((page) => page.label);
				controller.updateTileLabels(labels);
			}
		}
	}

	async function reloadTileSources(indices) {
		if (viewer) {
			const sources = indices.map((index) => pages[index]);
			const resolvedSources = await resolveTileSources(sources);

			if (resolvedSources?.length) {
				resolvedSources.forEach((tileSource, i) => {
					const index = indices[i];
					const existing = viewer.world.getItemAt(index);
					viewer.addTiledImage({
						tileSource,
						index,
						replace: existing ? true : false
					});
				});
			}
		}
	}

	function updateTileLabels(pages) {
		const labels = pages.map((page) => page.label);

		if (controller) {
			controller.updateTileLabels(labels);
		}
	}

	function updateSpinners(spinners) {
		if (controller) {
			controller.updateSpinners(spinners);
		}
	}

	function resizeLabels() {
		if (controller) {
			controller.resizeTileLabels();
		}
	}

	function updateLabels() {
		const labels = pages.map((page) => page.label);
		if (controller) {
			controller.updateTileLabels(labels);
		}
	}

	function reflow(item) {
		const world = item.eventSource;
		const items = world.getItemCount();
		const cols = layout === 'grid' ? fitColumns(width / height, items) : 1;
		controller.updateCollectionLayout({ columns: cols });
		world.viewer.viewport.goHome();
		world.viewer.forceRedraw();
		if (!isFullyLoaded && items >= pages.length) {
			fullyLoaded.resolve();
			isFullyLoaded = true;
		}
		updateTileLabels(pages);
	}

	const debouncedReflow = debounce(reflow, 500);

	onMount(async () => {
		const { Point: OSDPoint, Placement: OSDPlacement } = await import('openseadragon');
		const { createController } = await import('./OSDController.js');
		const { default: CanvasOverlay } = await import('./canvas-overlay.js');
		const { default: getNearestTile } = await import('./get-nearest-tile.js');
		const { default: getPixelCoords } = await import('./get-pixel-coords.js');
		const { default: getTileCoords } = await import('./get-tile-coords.js');

		Point = OSDPoint;
		Placement = OSDPlacement;

		const resolvedTileSources = await resolveTileSources(pages);
		const tileLabels = pages.map((page) => page.label);
		const tileRotations = pages.map((page) => page.rotation);
		prevTileSources = resolvedTileSources;
		ready.resolve();

		hitCanvases = range(0, resolvedTileSources.length).map(() => {
			return document.createElement('canvas');
		});

		overallHitCanvas = document.createElement('canvas');

		hitContexts = hitCanvases.map((canvas) => {
			const context = canvas.getContext('2d');
			context.imageSmoothingEnabled = false;
			return context;
		});

		overallHitContext = overallHitCanvas.getContext('2d');
		overallHitContext.imageSmoothingEnabled = false;

		const ar = width / height;
		const cols = layout === 'grid' ? fitColumns(ar, resolvedTileSources.length) : 1;

		controller = await createController(element, {
			...options,
			panHorizontal: pannable,
			panVertical: pannable,
			collectionLayout: 'horizontal',
			collectionRows: null,
			collectionColumns: cols,
			debugMode: debug,
			tileSources: resolvedTileSources,
			hitContexts,
			overallHitContext,
			tileLabels,
			tileRotations
		});

		viewer = controller.viewer;

		viewer.addOnceHandler('open', () => {
			controller.updateTileLabels();
			controller.setRotations(pages.map((page) => page.rotation));
		});

		fullyLoaded.then(() => {
			if (initialPage != null) {
				controlBus.emit('zoom-to-page', initialPage);
			}

			if (initialBounds != null) {
				controlBus.emit('zoom-to-bounds', { points: initialBounds });
			}
		});

		viewer.addHandler('zoom', resizeLabels);
		viewer.addHandler('tile-loaded', updateLabels);
		viewer.world.addHandler('add-item', debouncedReflow);

		if (tileOverlay || overlay) {
			const options = { onTileRedraw: null, onRedraw: null };

			if (tileOverlay) {
				options.onTileRedraw = (opts) => {
					const canvas = opts.context.canvas;
					const w = canvas.clientWidth;
					const h = canvas.clientHeight;

					const hitCanvas = hitCanvases[opts.index];
					const hitContext = hitContexts[opts.index];

					if (!hitCanvas || !hitContext) return;

					hitCanvas.style.width = `${w}px`;
					hitCanvas.style.height = `${h}px`;
					hitCanvas.width = canvas.width;
					hitCanvas.height = canvas.height;
					const transform = opts.context.getTransform();
					hitContext.setTransform(transform);

					tileOverlay({ ...opts, hitContext });
				};
			}

			if (overlay) {
				options.onRedraw = (opts) => {
					const canvas = opts.context.canvas;
					const w = canvas.clientWidth;
					const h = canvas.clientHeight;

					if (!overallHitCanvas || !overallHitContext) return;

					overallHitCanvas.style.width = `${w}px`;
					overallHitCanvas.style.height = `${h}px`;
					overallHitCanvas.width = canvas.width;
					overallHitCanvas.height = canvas.height;
					const transform = opts.context.getTransform();
					overallHitContext.setTransform(transform);

					const zoom = viewer.viewport.getZoom();

					overlay({ ...opts, hitContext: overallHitContext, zoom });
				};
			}

			new CanvasOverlay(viewer, options);

			controlBus.on('redraw', () => {
				if (viewer) {
					viewer.forceRedraw();
				}
			});

			controlBus.on('reload-tiles', (indices) => {
				reloadTileSources(indices);
			});
		}

		nearestTileFunc = (pixelCoords) => {
			const pt = new Point(pixelCoords.x, pixelCoords.y);
			return getNearestTile(viewer, pt);
		};

		isPointVisibleFunc = (point) => {
			const pt = new Point(point.x, point.y);
			const vpPt = viewer.viewport.pointFromPixel(pt);
			const bounds = viewer.viewport.getBounds();
			return bounds.containsPoint(vpPt);
		};

		tileFunc = (index) => {
			return viewer.world.getItemAt(index);
		};

		tileExtentsFunc = (index) => {
			const item = viewer.world.getItemAt(index);
			if (item) {
				const size = item.getContentSize();
				const rotation = item._degreesSpring?.target.value || 0;
				const corners = [
					{ x: 0, y: 0 },
					{ x: size.x, y: 0 },
					{ x: size.x, y: -size.y },
					{ x: 0, y: -size.y }
				].map((corner) => rotatePoint(corner, -rotation));
				const xmax = Math.max(...corners.map((c) => c.x));
				const ymin = Math.min(...corners.map((c) => c.y));
				const xmin = Math.min(...corners.map((c) => c.x));
				const ymax = Math.max(...corners.map((c) => c.y));
				return { xmin, ymin, xmax, ymax };
			}
			return null;
		};

		orientToTileFunc = (tileIndex, tileCoords) => {
			const item = viewer.world.getItemAt(tileIndex);
			if (!item) return null;
			const rotation = item._degreesSpring?.target.value || 0;
			return rotatePoint({ x: tileCoords.x, y: -tileCoords.y }, -rotation);
		};

		orientFromTileFunc = (tileIndex, rotatedCoords) => {
			const item = viewer.world.getItemAt(tileIndex);
			if (!item) return null;
			const rotation = item._degreesSpring?.target.value || 0;
			const pt = rotatePoint(rotatedCoords, rotation);
			return { x: pt.x, y: -pt.y };
		};

		tileCoordsConvFunc = (tileCoords) => {
			return getPixelCoords(viewer, tileCoords);
		};

		pixelCoordsConvFunc = (pixelCoords, tileIndex) => {
			const pt = new Point(pixelCoords.x, pixelCoords.y);
			return getTileCoords(viewer, pt, tileIndex);
		};

		if (controller) {
			controller.attachControlBus(controlBus);
		}

		controlBus.emit('mount');
	});

	onDestroy(() => {
		controlBus.all.clear();
		if (viewer) {
			viewer.destroy();
		}
		if (controller && controlBus) {
			controller.detachControlBus(controlBus);
		}
	});

	$: updatePannable(pannable);
	$: updateTileSources(pages);
	$: updateTileLabels(pages);
	$: updateSpinners(spinners);
	$: controller?.attachControlBus(controlBus);
</script>

<div class="osd-container">
	<div class="viewer" bind:this={element} bind:clientWidth={width} bind:clientHeight={height}></div>
	{#await ready}
		<div class="spinner">
			<img alt="Loading..." src={spinner} />
		</div>
	{/await}
</div>

<style>
	.osd-container {
		width: 100%;
		height: 100%;
		user-select: none;
	}
	.spinner {
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
	}

	.spinner img {
		animation: spin 1s linear infinite;
	}

	@keyframes spin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}
	.viewer {
		width: 100%;
		height: 100%;
	}

	:global(.openseadragon-canvas) {
		outline: none;
	}
</style>
